import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ArrowRight, CalendarDays } from "lucide-react"
import { api } from "../../../../convex/_generated/api"

export const Route = createFileRoute("/_authed/admin/events")({
  component: AdminEvents,
})

function AdminEvents() {
  const events = useQuery(api.events.listAll)

  return (
    <div className="space-y-4">
      <div className="console-panel rounded-[1.35rem] p-5">
        <p className="console-kicker">Live ops</p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight">All events</h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-[color:var(--muted-foreground)]">
          Cross-host view for scheduled, active, and completed events.
        </p>
      </div>

      {events === undefined ? (
        <ListSkeleton rows={5} />
      ) : events.length === 0 ? (
        <div className="console-panel rounded-[1.35rem] border-dashed p-8 text-center text-sm text-[color:var(--muted-foreground)]">
          No events yet.
        </div>
      ) : (
        <ul className="console-panel divide-y divide-[color:var(--border)] overflow-hidden rounded-[1.35rem]">
          {events.map((event) => (
            <li key={event._id}>
              <Link
                to="/host/events/$eventId"
                params={{ eventId: event._id }}
                className="group flex items-center justify-between gap-4 px-4 py-3.5 transition-colors hover:bg-[color:var(--surface-soft)] focus-visible:bg-[color:var(--surface-soft)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <DateTile startsAt={event.startsAt} />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{event.name}</p>
                    <p className="truncate text-xs text-[color:var(--muted-foreground)]">
                      {new Date(event.startsAt).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide ${statusTone(event.status)}`}
                  >
                    {event.status === "cancelled" ? "canceled" : event.status}
                  </span>
                  <ArrowRight
                    className="hidden h-4 w-4 text-[color:var(--muted-foreground)] transition-transform group-hover:translate-x-0.5 sm:block"
                    aria-hidden
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function ListSkeleton({ rows }: { rows: number }) {
  return (
    <ul className="console-panel divide-y divide-[color:var(--border)] rounded-[1.35rem]">
      {Array.from({ length: rows }).map((_, index) => (
        <li key={index} className="flex items-center gap-3 px-4 py-3.5">
          <div className="h-12 w-12 animate-pulse rounded-2xl bg-[color:var(--muted)]" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-48 max-w-full animate-pulse rounded bg-[color:var(--muted)]" />
            <div className="h-2.5 w-32 animate-pulse rounded bg-[color:var(--muted)]" />
          </div>
        </li>
      ))}
    </ul>
  )
}

function DateTile({ startsAt }: { startsAt: number }) {
  const date = new Date(startsAt)
  return (
    <span className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface-soft)] text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
      <CalendarDays className="mb-0.5 h-3.5 w-3.5 text-[color:var(--primary)]" aria-hidden />
      <span className="text-[10px] font-bold uppercase leading-none text-[color:var(--muted-foreground)]">
        {date.toLocaleDateString(undefined, { month: "short" })}
      </span>
      <span className="text-sm font-bold leading-none tabular-nums">
        {date.getDate()}
      </span>
    </span>
  )
}

function statusTone(status: string) {
  const tone: Record<string, string> = {
    scheduled:
      "border-[color:var(--border)] text-[color:var(--muted-foreground)]",
    ready: "border-amber-300 bg-amber-50 text-amber-800",
    active: "border-emerald-300 bg-emerald-50 text-emerald-800",
    completed:
      "border-[color:var(--border)] text-[color:var(--muted-foreground)]",
    cancelled: "border-red-200 bg-red-50 text-red-700",
  }
  return tone[status] ?? tone.scheduled
}
